import * as React from 'react';
import clsx from 'clsx';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { Button } from '@/components/ui/Button';
import microphoneIcon from '@/assets/images/svg/microphone_var_2.svg';
import { useVoiceRecorder } from '@/hooks/useVoiceRecorder';
import { WaveformPlayer } from '@/components/audio/WaveformPlayer';
import languageSamples from '@/assets/json/voice-cloning-language-samples.json';
import { formatDuration } from '@/utils/formatters';
import { useVoiceCloneOptions } from '../../../../hooks/useVoiceCloneOptions';

import type { VoiceFileSelectHandler } from '../../../../types';

interface RecordingViewProps {
  onSave: VoiceFileSelectHandler;
}

/** Extra words to highlight ahead of recognition to compensate for SpeechRecognition latency. */
const LEAD_OFFSET = 2;

const samples = (languageSamples as { samples: Record<string, string> }).samples;

export const RecordingView: React.FC<RecordingViewProps> = ({ onSave }) => {
  const {
    isRecording,
    recordingTime,
    audioData,
    spokenWordCount,
    startRecording,
    stopRecording,
    cancelRecording,
    error,
  } = useVoiceRecorder();

  const [recordedBlob, setRecordedBlob] = React.useState<Blob | null>(null);
  const hasStarted = isRecording || recordedBlob !== null;

  // ── Dynamic language/accent data ──────────────────────────────
  const { languages, accentsByLang, optionsLoading } = useVoiceCloneOptions();

  // ── Selected state ────────────────────────────────────────────
  const [selectedLangCode, setSelectedLangCode] = React.useState('en');
  const [selectedAccent, setSelectedAccent] = React.useState('american');
  const [isLangDropdownOpen, setIsLangDropdownOpen] = React.useState(false);
  const [isAccentDropdownOpen, setIsAccentDropdownOpen] = React.useState(false);
  const langDropdownRef = React.useRef<HTMLDivElement>(null);
  const accentDropdownRef = React.useRef<HTMLDivElement>(null);

  const activeLangName =
    languages.find((lang) => lang.code === selectedLangCode)?.name ?? 'English';
  const accentsForLang = accentsByLang[selectedLangCode] ?? [];
  const sampleText = samples[selectedLangCode] ?? samples['en'] ?? '';
  const readingWords = sampleText.split(/\s+/);

  // Reset accent when language changes
  React.useEffect(() => {
    const accents = accentsByLang[selectedLangCode];
    if (accents && accents.length > 0) {
      setSelectedAccent(accents.includes('standard') ? 'standard' : (accents[0] ?? 'standard'));
    } else {
      setSelectedAccent('standard');
    }
  }, [selectedLangCode, accentsByLang]);

  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (langDropdownRef.current && !langDropdownRef.current.contains(e.target as Node)) {
        setIsLangDropdownOpen(false);
      }
      if (accentDropdownRef.current && !accentDropdownRef.current.contains(e.target as Node)) {
        setIsAccentDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleStartRecording = () => {
    setRecordedBlob(null);
    startRecording();
  };

  const handleStopRecording = async () => {
    const result = await stopRecording();
    if (result) {
      setRecordedBlob(result.blob);
    }
  };

  const handleRetake = () => {
    cancelRecording();
    setRecordedBlob(null);
  };

  const handleSave = () => {
    if (recordedBlob) {
      const fileType = recordedBlob.type || 'audio/webm';
      const file = new File([recordedBlob], 'voice_clone.webm', { type: fileType });
      onSave(file, activeLangName, selectedAccent);
    }
  };

  const capitalize = (str: string) => str.replace(/\b\w/g, (ch) => ch.toUpperCase());

  if (optionsLoading) {
    return (
      <div className='flex items-center justify-center py-12'>
        <div className='size-6 animate-spin rounded-full border-2 border-brand-800/20 border-t-brand-800' />
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-6'>
      <div className='flex items-center justify-between gap-2'>
        <div className='flex items-center gap-2'>
          <img src={microphoneIcon} alt='' className='size-4 object-contain' />
          <div className='flex flex-wrap items-baseline gap-1'>
            <span className='font-sans text-12 font-semibold leading-24 text-brand-800'>
              Read this aloud:
            </span>
            <span className='font-sans text-12 font-normal leading-24 text-brand-800'>
              Speak naturally, like you&apos;re talking to a fan
            </span>
          </div>
        </div>

        <div className='flex items-center gap-2'>
          {/* Language Picker */}
          <div ref={langDropdownRef} className='relative'>
            <button
              type='button'
              onClick={() => setIsLangDropdownOpen((prev) => !prev)}
              disabled={isRecording}
              className={clsx(
                'flex items-center gap-1.5 rounded-10 border border-brand-800/20 px-3 py-1.5',
                'bg-white font-sans text-13 font-medium text-brand-800',
                'transition-colors hover:border-brand-800/40',
                'disabled:cursor-not-allowed disabled:opacity-50'
              )}
            >
              {activeLangName}
              <ChevronDownIcon
                className={clsx(
                  'size-3.5 transition-transform',
                  isLangDropdownOpen && 'rotate-180'
                )}
              />
            </button>

            {isLangDropdownOpen && (
              <div
                className={clsx(
                  'absolute right-0 top-full z-20 mt-1 max-h-[300px] min-w-[160px]',
                  'custom-scrollbar overflow-y-auto',
                  'rounded-10 border border-brand-800/15 bg-white py-1 shadow-lg'
                )}
              >
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    type='button'
                    onClick={() => {
                      setSelectedLangCode(lang.code);
                      setIsLangDropdownOpen(false);
                    }}
                    className={clsx(
                      'flex w-full items-center gap-2 px-3 py-2 text-left',
                      'font-sans text-13 transition-colors hover:bg-brand-50',
                      lang.code === selectedLangCode
                        ? 'font-semibold text-brand-800'
                        : 'font-normal text-brand-800/70'
                    )}
                  >
                    {lang.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Accent Picker */}
          {accentsForLang.length > 0 && (
            <div ref={accentDropdownRef} className='relative'>
              <button
                type='button'
                onClick={() => setIsAccentDropdownOpen((prev) => !prev)}
                disabled={isRecording}
                className={clsx(
                  'flex items-center gap-1.5 rounded-10 border border-brand-800/20 px-3 py-1.5',
                  'bg-white font-sans text-13 font-medium text-brand-800',
                  'transition-colors hover:border-brand-800/40',
                  'disabled:cursor-not-allowed disabled:opacity-50'
                )}
              >
                {capitalize(selectedAccent)}
                <ChevronDownIcon
                  className={clsx(
                    'size-3.5 transition-transform',
                    isAccentDropdownOpen && 'rotate-180'
                  )}
                />
              </button>

              {isAccentDropdownOpen && (
                <div
                  className={clsx(
                    'absolute right-0 top-full z-20 mt-1 max-h-[240px] min-w-[180px]',
                    'custom-scrollbar overflow-y-auto',
                    'rounded-10 border border-brand-800/15 bg-white py-1 shadow-lg'
                  )}
                >
                  {accentsForLang.map((accent) => (
                    <button
                      key={accent}
                      type='button'
                      onClick={() => {
                        setSelectedAccent(accent);
                        setIsAccentDropdownOpen(false);
                      }}
                      className={clsx(
                        'flex w-full px-3 py-2 text-left',
                        'font-sans text-13 transition-colors hover:bg-brand-50',
                        accent === selectedAccent
                          ? 'font-semibold text-brand-800'
                          : 'font-normal text-brand-800/70'
                      )}
                    >
                      {capitalize(accent)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className='flex flex-col gap-2'>
        <div className='max-h-[160px] w-full overflow-hidden rounded-20 border border-gray-535 bg-white shadow-sm'>
          <div className='custom-scrollbar max-h-[160px] overflow-y-auto p-6'>
            {sampleText ? (
              <p className='font-sans text-16 font-normal leading-24 tracking-normal'>
                {readingWords.map((word, i) => {
                  const highlighted = recordedBlob
                    ? true
                    : spokenWordCount > 0 && i < spokenWordCount + LEAD_OFFSET;

                  return (
                    <React.Fragment key={`${selectedLangCode}-${i}`}>
                      {i > 0 && ' '}
                      <span
                        className={clsx(
                          'transition-colors duration-300',
                          highlighted ? 'text-purple-400' : 'text-brand-800/40'
                        )}
                      >
                        {word}
                      </span>
                    </React.Fragment>
                  );
                })}
              </p>
            ) : (
              <p className='font-sans text-14 italic text-brand-800/40'>
                No sample text available for this language. Speak freely for 10-15 seconds.
              </p>
            )}
          </div>
        </div>
        <span className='font-sans text-12 font-normal leading-27 text-brand-800'>
          Cost 10 credits
        </span>
      </div>

      {!hasStarted ? (
        <div className='mt-4 flex flex-col items-center gap-4'>
          <button
            type='button'
            onClick={handleStartRecording}
            className={clsx(
              'flex size-[80px] items-center justify-center rounded-full',
              'bg-brand-750 transition-transform hover:scale-105 active:scale-95',
              'shadow-lg'
            )}
            aria-label='Start Recording'
          >
            <img src={microphoneIcon} alt='' className='size-8 brightness-0 invert' />
          </button>
          <span className='font-sans text-14 font-medium text-brand-800'>
            Tap to start recording
          </span>
        </div>
      ) : (
        <>
          <div className='mt-4 flex w-full flex-col items-center gap-2'>
            <div
              className={clsx(
                'flex h-[64px] w-full items-center justify-between px-6',
                'rounded-16 border-2 border-white',
                'bg-purple-130'
              )}
            >
              {isRecording ? (
                <div className='flex h-full flex-1 items-center gap-[3px] overflow-hidden pr-4'>
                  {audioData.map((amplitude, i) => (
                    <div
                      key={i}
                      className='w-[4px] min-w-[4px] rounded-full bg-brand-750 transition-all duration-75'
                      style={{ height: `${amplitude}%` }}
                    />
                  ))}
                </div>
              ) : recordedBlob ? (
                <div className='w-full min-w-0 flex-1 pr-4'>
                  <WaveformPlayer
                    url={recordedBlob}
                    height={40}
                    barWidth={4}
                    barGap={3}
                    waveColor='#1A1036'
                    progressColor='#764ba2'
                    buttonClassName='text-brand-800'
                    textClassName='text-brand-800'
                  />
                </div>
              ) : (
                <div className='flex-1' />
              )}

              {isRecording && (
                <button
                  onClick={handleStopRecording}
                  className={clsx(
                    'flex size-10 shrink-0 items-center justify-center rounded-full',
                    'bg-brand-750 transition-transform hover:scale-105 active:scale-95'
                  )}
                  aria-label='Stop Recording'
                >
                  <div className='size-3 rounded-[2px] bg-white' />
                </button>
              )}
            </div>

            <div className='flex w-full justify-between px-2 font-sans text-12 font-medium leading-20 text-brand-800'>
              <span>
                {error ? (
                  <span className='text-red-500'>{error}</span>
                ) : isRecording ? (
                  'Recording...'
                ) : (
                  'Review Audio'
                )}
              </span>
              {isRecording && <span>{formatDuration(recordingTime, { padMinutes: true })}</span>}
            </div>
          </div>

          <div className='mt-2 flex justify-center gap-4'>
            {!isRecording && (
              <Button
                onClick={handleRetake}
                variant='purple'
                className={clsx(
                  'h-[48px] w-[120px] rounded-[30px]',
                  'bg-gradient-to-r from-purple-300 to-brand-420',
                  'font-sans text-16 font-bold text-white',
                  'transition-transform hover:scale-105 active:scale-95'
                )}
              >
                Retake
              </Button>
            )}
            <Button
              onClick={handleSave}
              variant='settings-modify'
              className='h-[48px] w-[154px] text-[16px] text-brand-800'
              disabled={isRecording || !recordedBlob || recordingTime < 1}
            >
              Save Voice
            </Button>
          </div>
        </>
      )}
    </div>
  );
};
